import React, { Component } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import GetAppIcon from '@material-ui/icons/GetApp';

class GalleryModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentIndex: props.initialIndex || 0,
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate(prevProps) {
    const { isOpen, initialIndex } = this.props;
    // Reset current index when modal opens with new images
    if (isOpen && (prevProps.isOpen !== isOpen || prevProps.initialIndex !== initialIndex)) {
      this.setState({ currentIndex: initialIndex || 0 });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event) => {
    const { isOpen, onClose } = this.props;
    if (!isOpen) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.goToPrevious();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.goToNext();
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  };

  goToPrevious = () => {
    const { images } = this.props;
    this.setState((prevState) => ({
      currentIndex: (prevState.currentIndex - 1 + images.length) % images.length,
    }));
  };

  goToNext = () => {
    const { images } = this.props;
    this.setState((prevState) => ({
      currentIndex: (prevState.currentIndex + 1) % images.length,
    }));
  };

  goToImage = (index) => {
    this.setState({ currentIndex: index });
  };

  handleDownload = async () => {
    const { images } = this.props;
    const { currentIndex } = this.state;
    const imageUrl = images[currentIndex];

    if (imageUrl) {
      try {
        // Add cache-busting parameter to avoid cached responses
        const urlWithCacheBust = `${imageUrl}?t=${Date.now()}`;

        const response = await fetch(urlWithCacheBust, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `image-${currentIndex + 1}.png`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);
      } catch (error) {
        // Fallback: open in new tab
        const link = document.createElement('a');
        link.href = imageUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  render() {
    const { isOpen, images, onClose } = this.props;
    const { currentIndex } = this.state;

    if (!isOpen || !images || images.length === 0) {
      return null;
    }

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Sofia-pro, sans-serif',
        }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10000,
          }}
        >
          <CloseIcon style={{ color: '#fff', fontSize: 24 }} />
        </button>

        {/* Main image area */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            width: '100%',
            maxHeight: 'calc(100vh - 120px)', // Leave space for thumbnails
          }}
        >
          {/* Navigation arrows */}
          <button
            type="button"
            onClick={this.goToPrevious}
            style={{
              position: 'absolute',
              left: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 10000,
            }}
          >
            <ChevronLeftIcon style={{ color: '#fff', fontSize: 24 }} />
          </button>

          <button
            type="button"
            onClick={this.goToNext}
            style={{
              position: 'absolute',
              right: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 10000,
            }}
          >
            <ChevronRightIcon style={{ color: '#fff', fontSize: 24 }} />
          </button>

          {/* Main image with download button */}
          <div style={{
            position: 'relative', display: 'inline-block', margin: '50px 0', height: 'calc(100vh - 220px)',
          }}
          >
            <img
              src={images[currentIndex]}
              alt={`${currentIndex + 1} of ${images.length}`}
              style={{
                height: '100%',
                objectFit: 'contain',
                borderRadius: 8,
              }}
            />

            {/* Download button */}
            <button
              type="button"
              onClick={this.handleDownload}
              style={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                background: 'rgba(0, 0, 0, 0.7)',
                border: 'none',
                borderRadius: 4,
                width: 48,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10000,
              }}
              title="Download image"
            >
              <GetAppIcon style={{ color: '#fff', fontSize: 20 }} />
            </button>
          </div>

          {/* Image counter */}
          <div
            style={{
              position: 'absolute',
              bottom: 20,
              right: 20,
              background: 'rgba(0, 0, 0, 0.7)',
              color: '#fff',
              padding: '8px 12px',
              borderRadius: 4,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {currentIndex + 1} / {images.length}
          </div>
        </div>

        {/* Thumbnail tray */}
        <div
          style={{
            height: 100,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 20px',
            overflowX: 'auto',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              minWidth: 'fit-content',
            }}
          >
            {images.map((image, index) => (
              <button
                key={image}
                type="button"
                onClick={() => this.goToImage(index)}
                style={{
                  width: 80,
                  height: 80,
                  border: index === currentIndex ? '2px solid #fff' : '2px solid transparent',
                  borderRadius: 4,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: 'none',
                  padding: 0,
                  flexShrink: 0,
                }}
              >
                <img
                  src={image}
                  alt={`${index + 1} of ${images.length}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }
}

GalleryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};

export default GalleryModal; 
